import * as React from 'react';
import cn from 'classnames';
import { uniqueId } from 'lodash-es';
import { DetailedHTMLProps, LabelHTMLAttributes, useRef, useState } from 'react';
import { Checkmark, CheckboxIndeterminate } from '@tcl-boron-icons/icons';
import { BaseInputProps } from '@tcl-argon-models/models';
import './keyframes.scss';
import { BoldedLabel } from './BoldedLabel/BoldedLabel';
import { itemToString } from './coreUtils';
import { IHierarchicalDropdownOption } from '.';

type LabelProps = Omit<DetailedHTMLProps<LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>, 'id' | 'onChange'>;

export interface CheckboxProps extends Omit<BaseInputProps, 'theme'>, LabelProps {
  /** A unique ID. If none is provided, a unique id will be generated and prefixed with 'checkbox_' */
  id?: string;
  /** If true, mark the checkbox with a check icon. If false, checkbox will be an empty square. */
  isSelected?: boolean;
  /** A handler called whenever this input changes. The new value is passed as the only argument to the callback. */
  onChange?: (value: string) => void;
  /** The 'value' of this checkbox. Emitted whenever the onChange handler is triggered */
  value?: string;
  /** if true and element is also selected, the checkbox will appear in an indeterminate state. */
  /** used mainly for nested checkboxes */
  isIndeterminate?: boolean;
  /** sets tab index for checkbox. Used to override the styling for tabIndex in nested checkbox use cases */
  tabIndex?: number;
  inputValue: string;
  item: IHierarchicalDropdownOption;
  highlightedFromParent: boolean;
  shouldShowTreeStructure: boolean;
  isHierarchical: boolean;
}

export const SelectCheckbox: React.ForwardRefExoticComponent<CheckboxProps & React.RefAttributes<HTMLLabelElement>> =
  React.forwardRef(
    (
      {
        classes,
        disabled = false,
        id,
        isSelected = false,
        label,
        onChange = () => {},
        value,
        isIndeterminate = false,
        tabIndex,
        inputValue,
        item,
        highlightedFromParent,
        shouldShowTreeStructure,
        isHierarchical,
        ...props
      },
      ref,
    ) => {
      [id] = useState(id || uniqueId('checkbox_'));
      const [hovered, setHovered] = useState(false);
      const checkboxRef = useRef<HTMLInputElement>(null);

      const setHoverCheckmarkOnMouseEnter = () => {
        !isSelected ? setHovered(true) : setHovered(false);
      };

      const onClick = (value: string) => {
        if (isIndeterminate && checkboxRef && checkboxRef.current) {
          checkboxRef.current.indeterminate = true;
          setHovered(false);
        } else if (isSelected && checkboxRef && checkboxRef.current) {
          checkboxRef.current.checked = true;
          setHovered(false);
        }
        onChange(value);
      };

      return (
        <label
          htmlFor={id}
          className={cn(classes.selectCheckboxLabel, classes.flexbox, {
            [classes.selectCheckboxHierarchicalLabel]: isHierarchical,
            [classes.treeStructureSelectedCheckboxLabel]: shouldShowTreeStructure,
          })}
          data-analytics="checkbox"
          onMouseEnter={() => setHoverCheckmarkOnMouseEnter()}
          onMouseLeave={() => setHovered(false)}
          ref={ref}
          {...props}
        >
          <input
            checked={isSelected}
            className={classes.input}
            disabled={disabled}
            id={id}
            onChange={(e) => onClick(e.target.value)}
            type="checkbox"
            value={value}
            ref={checkboxRef}
            tabIndex={tabIndex}
            aria-checked={isSelected && !isIndeterminate ? 'true' : isSelected && isIndeterminate ? 'mixed' : 'false'}
            role={'checkbox'}
          />
          <div className={classes.multiSelectCheckboxWrapper}>
            <div
              className={cn(classes.checkboxContainer, 'checkboxInputFocusStyles', {
                [classes.checkboxContainerDisabledSelected]: isSelected && disabled,
                [classes.checkboxContainerSelected]: isSelected && !disabled,
                [classes.checkboxContainerDisabledUnselected]: !isSelected && disabled,
                [classes.checkboxContainerUnselected]: !isSelected && !disabled,
              })}
            >
              {isSelected && !isIndeterminate && (
                <Checkmark
                  className={cn(classes.svg, classes.icon, { [classes.svgDisabled]: disabled })}
                  height={12}
                  width={12}
                />
              )}
              {isSelected && isIndeterminate && (
                <CheckboxIndeterminate
                  className={cn(classes.svg, classes.icon, { [classes.svgDisabled]: disabled })}
                  height={12}
                  width={12}
                />
              )}
              {(hovered || highlightedFromParent) && !isSelected && (
                <Checkmark className={cn(classes.hoverCheck, classes.icon)} height={12} width={12} />
              )}
            </div>
          </div>
          <BoldedLabel
            highlightJustBeginning={false}
            inputValue={inputValue}
            optionLabel={`${itemToString(item)}`}
            className={cn(classes.boldedLabel, {
              [classes.boldedLabelHierarchicalWithLink]: item.hasParent || item.children,
            })}
          />
        </label>
      );
    },
  );
SelectCheckbox.displayName = 'SelectCheckbox';
